/** Organisations — REAL data. This replaces `fixtures.ts` and
 *  `detail_fixtures.ts`, which between them invented eighteen companies.
 *
 *  Dattatray: *"Why there are these organisations?"* and then, plainly:
 *  *"I dont want any emulated/hardcoded stuff on screen. Show me the real
 *  deal!"* Batterysmart, Nexcell Pack Works, Pune Service Hub, EVE Energy,
 *  Sinowatt Cells and Kolhapur Assembly never existed. The database holds
 *  CMP-001 Aphelion Energy, CMP-002 GO Electric and TEST-CO Orbit Test Co.
 *
 *  A page of plausible fake companies is indistinguishable from a real one in
 *  a screenshot, which is exactly how it survived review — and why the fix is
 *  DELETING the fixture modules rather than correcting their contents.
 */
import { useQuery } from "@tanstack/react-query";

import { organisations } from "@/api/client";

export type { AllocationRow, CreatedUser, ManufacturerFacet, OrganisationDetail, OrganisationRow, OrgUserRow } from "@/api/client";

/** Definitions and directory data, not telemetry — refetched on invalidation
 *  rather than on a timer (UI performance laws, ARCHITECTURE.md). */
const STATIC = { staleTime: 60_000, refetchOnWindowFocus: false } as const;

export const orgQk = {
  list: ["organisations"] as const,
  detail: (id: string) => ["organisations", id] as const,
  users: (id?: string) => ["organisations", "users", id ?? "all"] as const,
  manufacturer: (id: string) => ["organisations", id, "manufacturer"] as const,
};

export function useOrganisations() {
  return useQuery({ queryKey: orgQk.list, queryFn: organisations.list, ...STATIC });
}

export function useOrganisation(id: string) {
  return useQuery({
    queryKey: orgQk.detail(id), queryFn: () => organisations.detail(id),
    enabled: !!id, ...STATIC,
  });
}

export function useManufacturerFacet(cid: string) {
  return useQuery({
    queryKey: orgQk.manufacturer(cid),
    queryFn: () => organisations.manufacturer(cid),
    enabled: !!cid, ...STATIC,
  });
}

export function useOrgUsers(companyId?: string) {
  return useQuery({
    queryKey: orgQk.users(companyId), queryFn: () => organisations.users(companyId),
    ...STATIC,
  });
}

export const ROLE_LABEL: Record<string, string> = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  company_user: "Company User",
};
